import { CircleHelpIcon, WebhookIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useWebhooks from '@/hooks/useWebhooks';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';

type PersonalizationWebhookInfoProps = { helpTooltipSide?: 'top' | 'right' | 'bottom' | 'left' };

export function PersonalizationWebhookInfo({
  helpTooltipSide = 'right',
}: PersonalizationWebhookInfoProps = {}) {
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const { t } = useTranslation();

  // Use the useWebhooks hook to fetch all webhooks
  const { webhooks } = useWebhooks(brainId);

  // Find first_message webhook
  const firstMessageWebhook = webhooks?.find((webhook) => webhook.type === 'first_message');

  const renderContent = () => {
    if (!webhooks) {
      return <Skeleton className="h-6 w-32" />;
    }

    if (!firstMessageWebhook) {
      return (
        <Link to={`/${slug}/ai-agents/${brainId}/workflows/webhooks/new?type=first_message`}>
          <Button
            type="button"
            variant="link-secondary"
            size="flush"
            className="justify-start text-sm leading-6 font-medium text-foreground"
          >
            {t('common.create')}
          </Button>
        </Link>
      );
    }

    return (
      <Link
        to={`/${slug}/ai-agents/${brainId}/workflows/webhooks/${firstMessageWebhook.webhook_id}`}
      >
        <Button
          type="button"
          variant="link-secondary"
          className="m-0 h-auto justify-start p-0 text-sm leading-6 font-medium text-foreground"
        >
          <span className="flex max-w-[136px] items-center justify-start gap-2">
            <WebhookIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{firstMessageWebhook.name}</span>
          </span>
        </Button>
      </Link>
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1 text-xs leading-4 font-medium text-muted-foreground">
        {t('ai_agents.personalization')}
        <InfoTooltip content={t('ai_agents.guidelines.webhooks_tooltip')} side={helpTooltipSide}>
          <CircleHelpIcon size={12} className="text-muted-foreground" />
        </InfoTooltip>
      </div>
      <div className="h-6">{renderContent()}</div>
    </div>
  );
}
